// lingland-frontend/src/hooks/useBlogPosts.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { blogContent } from '../pages/BlogContentData';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const useBlogPosts = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { i18n } = useTranslation();

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/blog`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                
                // Mapuj dane z API do formatu oczekiwanego przez komponenty
                const mappedPosts = data.map(post => ({
                    id: post.id,
                    slug: post.slug,
                    title: i18n.language === 'en' && post.title_en ? post.title_en : post.title,
                    excerpt: i18n.language === 'en' && post.excerpt_en ? post.excerpt_en : post.excerpt,
                    image: post.featured_image_url ? `${API_BASE_URL}${post.featured_image_url}` : null,
                    date: new Date(post.published_at).toISOString().split('T')[0], // Format YYYY-MM-DD
                    categories: post.categories || []
                }));
                
                setPosts(mappedPosts);
                setError(null);
            } catch (err) {
                console.error('Error fetching blog posts:', err);
                console.log('API_BASE_URL:', API_BASE_URL);
                setError(err.message);
                setPosts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, [i18n.language]);

    return { posts, loading, error };
};

export const useBlogPost = (slug) => {
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { i18n } = useTranslation();

    useEffect(() => {
        if (!slug) return;

        const fetchPost = async () => {
            try {
                setLoading(true);
                // Pobierz metadane z API
                const response = await fetch(`${API_BASE_URL}/api/blog/${slug}`);
                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Post not found');
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                
                // Pobierz treść z lokalnego pliku
                const currentLanguage = i18n.language;
                const content = blogContent[currentLanguage] && blogContent[currentLanguage][slug] 
                    ? blogContent[currentLanguage][slug] 
                    : blogContent['pl'][slug]; // fallback do polskiego
                
                // Mapuj dane - metadane z API, treść z pliku
                const mappedPost = {
                    id: data.id,
                    slug: data.slug,
                    title: i18n.language === 'en' && data.title_en ? data.title_en : data.title,
                    excerpt: i18n.language === 'en' && data.excerpt_en ? data.excerpt_en : data.excerpt,
                    content: content, // treść z lokalnego pliku
                    image: data.featured_image_url ? `${API_BASE_URL}${data.featured_image_url}` : null,
                    date: new Date(data.published_at).toISOString().split('T')[0],
                    published_at: data.published_at,
                    categories: data.categories || [],
                    seo: {
                        title: i18n.language === 'en' && data.seo_title_en ? data.seo_title_en : data.seo_title_pl,
                        description: i18n.language === 'en' && data.seo_description_en ? data.seo_description_en : data.seo_description_pl
                    }
                };
                
                setPost(mappedPost);
                setError(null);
            } catch (err) {
                console.error(`Error fetching blog post ${slug}:`, err);
                setError(err.message);
                setPost(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug, i18n.language]);

    return { post, loading, error };
};
